import React, { useState } from "react";
import { T } from "../../app/tokens.js";
import { buildLeaseUrl } from "../../services/buildUrl.js";
import { InternalLink } from "../../components/InternalLink.jsx";
import { formatFootprintSqftFull as formatSqftFull } from "../../utils/format.js";
import { currentLeaseFootprint } from "../../../shared/leaseAreaBasis.js";
import { leaseAreaPresentation } from "../leases/leaseAreaPresentation.js";
import { AreaLifecycleBadge } from "../leases/components/AreaLifecycleBadge.jsx";

function leaseSizeText(lease, asOf) {
  const size = currentLeaseFootprint(lease, asOf).grossSf;
  return size != null && Number.isFinite(size) ? formatSqftFull(size) : "—";
}

function areaSizeText(area) {
  const size = area?.sizeSf == null ? null : Number(area.sizeSf);
  return size != null && Number.isFinite(size) ? formatSqftFull(size) : "—";
}

/**
 * Shared Areas strip used by lease Overview and office Overview.
 * The office variant accepts flattened area rows carrying ownerLeaseId.
 */
export function LeaseAreasStrip({
  lease,
  location,
  siblingLeases = [],
  officeAreas = null,
  wrapperStyle,
  onEditAreas,
  asOf = new Date(),
  // Task #8581: office Overview only. Lease detail never passes this, so its
  // always-open presentation is unchanged.
  collapsible = false,
}) {
  const [expanded, setExpanded] = useState(false);
  const officeMode = Array.isArray(officeAreas);
  const areas = officeMode ? officeAreas : (Array.isArray(lease?.areas) ? lease.areas : []);
  const lifecycle = leaseAreaPresentation({ areas }, asOf);
  const hasDetails = areas.length > 0;
  const canToggle = officeMode && collapsible && hasDetails;
  const detailsVisible = hasDetails && (!canToggle || expanded);
  const premises = [
    location?.suite,
    location?.premises,
    lease?.premises,
  ].find((value) => typeof value === "string" && value.trim())?.trim();
  const areaNames = lifecycle.active.map((area) => area?.name?.trim()).filter(Boolean);
  const summary = areas.length
    ? (areaNames.length ? areaNames.join(" · ")
      : lifecycle.activeCount ? "Active area details unavailable"
        : lifecycle.groups.every((group) => group.status === "unknown") ? "Area lifecycle unavailable" : "No active areas")
    : (premises || "Area details unavailable");
  const siblings = officeMode
    ? []
    : siblingLeases.filter((other) => other?.id && other.id !== lease?.id);
  const stripTestId = officeMode ? "office-areas-strip" : "lease-areas-strip";
  const summaryTestId = officeMode ? "office-areas-summary" : "lease-areas-summary";
  const totalTestId = officeMode ? "office-areas-total" : "lease-areas-total";
  const detailsTestId = officeMode ? "office-areas-details" : "lease-areas-details";
  const rowTestId = officeMode ? "office-area-detail-row" : "lease-area-detail-row";
  const totalLease = officeMode ? { size: lease?.size } : lease;
  // A native button gives keyboard (Enter/Space) toggling for free. Office
  // mode carries no sibling links, so no interactive content nests inside it.
  const StripTag = canToggle ? "button" : "div";
  const stripToggleProps = canToggle
    ? {
      type: "button",
      "aria-expanded": expanded,
      "aria-controls": detailsTestId,
      onClick: () => setExpanded((v) => !v),
    }
    : {};

  return (
    <div
      data-testid={officeMode ? "office-areas-strip-wrap" : "lease-areas-strip-wrap"}
      style={{ margin: "0 14px 12px", flexShrink: 0, ...wrapperStyle }}
    >
      <StripTag
        {...stripToggleProps}
        id={officeMode ? "office-areas-strip" : "lease-areas-strip"}
        data-testid={stripTestId}
        style={{
          ...(canToggle && {
            width: "100%", textAlign: "left", font: "inherit", cursor: "pointer",
            borderRadius: expanded ? "8px 8px 0 0" : 8,
          }),
          display: "flex",
          alignItems: "center",
          gap: 9,
          flexWrap: "wrap",
          background: T.tint,
          border: `1px solid ${T.border}`,
          borderRadius: 8,
          padding: "8px 10px",
          minHeight: 44,
          boxSizing: "border-box",
        }}
      >
        {canToggle && (
          // Same caret glyph/rotation as the Home space pipeline expander.
          <span
            aria-hidden="true"
            data-testid="office-areas-caret"
            style={{
              display: "inline-block",
              color: T.mid,
              fontSize: T.fsXs,
              transition: "transform 0.15s ease",
              transform: expanded ? "rotate(0deg)" : "rotate(-90deg)",
              flexShrink: 0,
            }}
          >
            ▾
          </span>
        )}
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            color: T.dark,
            fontSize: T.fsXs,
            fontWeight: T.fwBold,
            flexShrink: 0,
          }}
        >
          <span
            aria-hidden="true"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 19,
              height: 19,
              borderRadius: 5,
              background: T.blueSoft,
              color: T.blue,
              fontSize: T.fsXxs,
            }}
          >
            {lifecycle.activeCount}
          </span>
          {" "}active {lifecycle.activeCount === 1 ? "area" : "areas"}
          {lifecycle.expiredCount > 0 && <span style={{ color: T.light, fontWeight: T.fwRegular }}>
            {" - "}{lifecycle.expiredCount} expired
          </span>}
        </span>
        <span
          data-testid={summaryTestId}
          title={summary}
          style={{
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: T.mid,
            fontSize: T.fsXs,
          }}
        >
          {summary}
          {siblings.map((other) => {
            const otherArea = leaseAreaPresentation(other, asOf).active.find((area) => area?.name?.trim())?.name?.trim();
            return (
              <React.Fragment key={other.id}>
                {" · "}
                <span>{otherArea || "Other area"} is a separate lease here · </span>
                <InternalLink
                  data-testid="lease-areas-sibling-link"
                  href={buildLeaseUrl(other.id)}
                  style={{ color: T.blue, fontWeight: T.fwSemibold, textDecoration: "none" }}
                >
                  {other.id} →
                </InternalLink>
              </React.Fragment>
            );
          })}
        </span>
        <span
          data-testid={totalTestId}
          title="Current qualifying footprint; all recorded areas remain in the details"
          style={{
            color: T.dark,
            fontSize: T.fsXs,
            fontWeight: T.fwBold,
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          {leaseSizeText(totalLease, asOf)}
        </span>
        {!officeMode && onEditAreas && areas.length > 0 && <button type="button"
          onClick={onEditAreas} style={{ background: "none", border: 0, color: T.blue,
            fontSize: T.fsXs, cursor: "pointer" }}>Edit areas</button>}
      </StripTag>
      {detailsVisible && (
        <div
          id={detailsTestId}
          data-testid={detailsTestId}
          style={{
            padding: "3px 10px 7px",
            background: T.card,
            border: `1px solid ${T.border}`,
            borderTop: 0,
            borderRadius: "0 0 8px 8px",
          }}
        >
          {lifecycle.groups.map((group) => <React.Fragment key={group.status}>
            <div style={{ color: T.light, fontSize: T.fsXxs, fontWeight: T.fwSemibold,
              padding: "10px 1px 3px" }}>{group.label}</div>
          {group.areas.map((area, index) => {
            const deskCount = area?.deskCount ?? area?.desks;
            const expired = area.status === "expired";
            return (
              <div
                key={`${area?.id || area?.name || "area"}-${area?.ownerLeaseId || index}`}
                data-testid={rowTestId}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 10,
                  ...(officeMode && { flexWrap: "wrap", overflowWrap: "anywhere" }),
                  padding: "7px 1px",
                  borderBottom: index < group.areas.length - 1 ? `1px solid ${T.border}` : 0,
                  fontSize: T.fsXs,
                }}
              >
                <span style={{ minWidth: 0, color: expired ? T.light : T.dark, fontWeight: T.fwSemibold }}>
                  {area.status === "active" && <AreaLifecycleBadge area={area} />}
                  {area?.name?.trim() || "Area"}
                  {area.status !== "active" && <AreaLifecycleBadge area={area} />}
                  {deskCount != null && deskCount !== "" && (
                    <span data-testid="lease-area-desk-count" style={{ color: T.mid, fontWeight: T.fwRegular }}>
                      {" · "}{deskCount} desks
                    </span>
                  )}
                  {officeMode && area?.ownerLeaseId && (
                    <InternalLink
                      data-testid="office-area-lease-link"
                      href={buildLeaseUrl(area.ownerLeaseId)}
                      style={{
                        marginLeft: 9,
                        color: T.blue,
                        fontWeight: T.fwSemibold,
                        textDecoration: "none",
                      }}
                    >
                      {area.ownerLeaseId} →
                    </InternalLink>
                  )}
                </span>
                <span style={{ color: expired ? T.light : T.dark, fontWeight: T.fwSemibold, whiteSpace: officeMode ? "normal" : "nowrap" }}>
                  {areaSizeText(area)}
                  {!expired && area.usableSizeSf != null && <span style={{ color: T.mid, fontWeight: T.fwRegular }}>
                    {" · "}{Number(area.usableSizeSf).toLocaleString()} usable sf
                  </span>}
                </span>
              </div>
            );
          })}
          </React.Fragment>)}
        </div>
      )}
    </div>
  );
}